import React, { useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { getConf } from '@/controller/configuration';

import Footer from './Footer';

const RootLayout = () => {
    const nav = useNavigate();
    const location = useLocation();

    const [y, setY] = useState(window.scrollY);
    const [logo, setLogo] = useState(null);
    const [banner, setBanner] = useState(null);

    const handleScroll = useCallback(
        (e) => {
            const header = document.getElementById('site-header-v2');
            const headerMobile = document.getElementById('site-header-mobile-v2');

            const window = e.currentTarget;

            if (window.scrollY === 0) {
                header?.classList.remove('hide');
                headerMobile?.classList.remove('hide');
            } else {
                if (y > window.scrollY) {
                    header?.classList.remove('hide');
                    headerMobile?.classList.remove('hide');
                } else if (y < window.scrollY) {
                    if (window.scrollY > 100) {
                        header?.classList.add('hide');
                        headerMobile?.classList.add('hide');
                    }
                }
            }
            setY(window.scrollY);
        },
        [y],
    );

    useEffect(() => {
        const loadBrandingAssets = async () => {
            try {
                const resp = await getConf();

                if (resp) {
                    if (resp.logo) {
                        localStorage.setItem('lg', resp.logo);
                        setLogo(resp.logo);
                    }

                    if (resp.banner) {
                        localStorage.setItem('bn', resp.banner);
                        setBanner(resp.banner);
                    } else {
                        localStorage.removeItem('bn');
                        setBanner(null);
                    }

                    if (resp.appid) {
                        localStorage.setItem('appid', resp.appid);
                    }

                    if (resp.business_name) {
                        localStorage.setItem('business_name', resp.business_name);
                        document.title = resp.business_name;
                    }

                    if (resp.frontend_url) {
                        localStorage.setItem('frontend_url', resp.frontend_url);
                    }
                }
            } catch (error) {
                console.error('Failed to load branding assets:', error);
                // Fallback to localStorage values if available
                const cachedLogo = localStorage.getItem('lg');
                const cachedBanner = localStorage.getItem('bn');
                if (cachedLogo) setLogo(cachedLogo);
                if (cachedBanner) setBanner(cachedBanner);
            }
        };

        loadBrandingAssets();
    }, []);

    useEffect(() => {
        setY(window.scrollY);
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    useEffect(() => {
        if (import.meta.env.VITE_APP_ENV === 'prod') {
            ReactGA.send({ hitType: 'pageview', page: location.pathname });
        }
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Mobile Header */}
            <header
                id="site-header-mobile-v2"
                className="lg:hidden bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50 transition-transform duration-300"
            >
                <div className="px-4 py-3">
                    <div className="flex items-center justify-center">
                        {/* Logo */}
                        <div className="w-8 h-8 text-blue-600">
                            {logo ? (
                                <img
                                    src={logo}
                                    alt="Logo"
                                    className="w-full h-full cursor-pointer"
                                    onClick={() => nav('/login')}
                                />
                            ) : (
                                <svg
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="cursor-pointer"
                                    onClick={() => nav('/login')}
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M24 18.4228L42 11.475V34.3663C42 34.7796 41.7457 35.1504 41.3601 35.2992L24 42V18.4228Z"
                                        fill="currentColor"
                                    ></path>
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M24 8.18819L33.4123 11.574L24 15.2071L14.5877 11.574L24 8.18819ZM9 15.8487L21 20.4805V37.6263L9 32.9945V15.8487ZM27 37.6263V20.4805L39 15.8487V32.9945L27 37.6263ZM25.354 2.29885C24.4788 1.98402 23.5212 1.98402 22.646 2.29885L4.98454 8.65208C3.7939 9.08038 3 10.2097 3 11.475V34.3663C3 36.0196 4.01719 37.5026 5.55962 38.098L22.9197 44.7987C23.6149 45.0671 24.3851 45.0671 25.0803 44.7987L42.4404 38.098C43.9828 37.5026 45 36.0196 45 34.3663V11.475C45 10.2097 44.2061 9.08038 43.0155 8.65208L25.354 2.29885Z"
                                        fill="currentColor"
                                    ></path>
                                </svg>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Desktop Header */}
            <header
                id="site-header-v2"
                className="hidden lg:block bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50 transition-transform duration-300"
            >
                <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo Section */}
                        <div className="flex items-center">
                            {logo ? (
                                <img
                                    src={logo}
                                    alt="Logo"
                                    className="h-10 cursor-pointer"
                                    onClick={() => nav('/login')}
                                />
                            ) : (
                                <div className="w-10 h-10 text-blue-600">
                                    <svg
                                        viewBox="0 0 48 48"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="cursor-pointer w-full h-full"
                                        onClick={() => nav('/login')}
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M24 18.4228L42 11.475V34.3663C42 34.7796 41.7457 35.1504 41.3601 35.2992L24 42V18.4228Z"
                                            fill="currentColor"
                                        ></path>
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M24 8.18819L33.4123 11.574L24 15.2071L14.5877 11.574L24 8.18819ZM9 15.8487L21 20.4805V37.6263L9 32.9945V15.8487ZM27 37.6263V20.4805L39 15.8487V32.9945L27 37.6263ZM25.354 2.29885C24.4788 1.98402 23.5212 1.98402 22.646 2.29885L4.98454 8.65208C3.7939 9.08038 3 10.2097 3 11.475V34.3663C3 36.0196 4.01719 37.5026 5.55962 38.098L22.9197 44.7987C23.6149 45.0671 24.3851 45.0671 25.0803 44.7987L42.4404 38.098C43.9828 37.5026 45 36.0196 45 34.3663V11.475C45 10.2097 44.2061 9.08038 43.0155 8.65208L25.354 2.29885Z"
                                            fill="currentColor"
                                        ></path>
                                    </svg>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content with Banner Underlay */}
            <div className="relative flex-1">
                {/* Banner Underlay */}
                {banner != null ? (
                    <div
                        className="absolute top-0 left-0 right-0 h-full bg-no-repeat bg-center bg-cover z-0"
                        style={{
                            backgroundImage: `url(${banner})`,
                        }}
                    >
                        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                    </div>
                ) : (
                    <div className="absolute top-0 left-0 right-0 h-full bg-gradient-to-r from-white-500 to-white-600 z-0">
                        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                    </div>
                )}

                {/* Main Content */}
                <main className="relative z-10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <Outlet />
                    </div>
                </main>
            </div>

            {/* Footer */}
            <footer className="mt-auto">
                <Footer />
            </footer>

            {/* Custom CSS for smooth hide animation */}
            <style>{`
        .hide {
          transform: translateY(-100%);
        }
      `}</style>
        </div>
    );
};

export default RootLayout;
